import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import useSignalR from "hooks/useSignalR";
import { getFetchOptions } from "utils/fetch";
import useIsDeveloper from "../useIsDeveloper";
import "./TranscriptBackfill.scss";

const SIGNALR_GROUP = "transcript-backfill";
const RUN_ID_STORAGE_KEY = "transcriptBackfillRunId";

// Mirror of lms.web.App.src.features.Developer.TranscriptBackfill.BackfillCandidatePhase.
// String values match the server-side enum names so phase comparisons stay readable.
const CANDIDATE_PHASE = Object.freeze({
	Pending: "Pending",
	Skipped: "Skipped",
	UnrecognisedPackageType: "UnrecognisedPackageType",
	Preparing: "Preparing",
	Uploading: "Uploading",
	Submitted: "Submitted",
	Transcribing: "Transcribing",
	Harvested: "Harvested",
	Failed: "Failed"
});

const TERMINAL_PHASES = new Set([
	CANDIDATE_PHASE.Skipped,
	CANDIDATE_PHASE.UnrecognisedPackageType,
	CANDIDATE_PHASE.Harvested,
	CANDIDATE_PHASE.Failed
]);

const formatAssociations = (associations) => {
	if (!associations || associations.length === 0) {
		return "—";
	}
	const courses = [...new Set(associations.map((a) => a.courseName || `Course ${a.courseId}`))];
	return courses.join(", ");
};

const formatMs = (value) => (value == null ? "—" : `${value}ms`);

// Column-key → row-accessor map used by the sortable table headers. Keep keys
// stable — they're referenced by SortableHeader's `column` prop.
const SORT_ACCESSORS = Object.freeze({
	contentId: (r) => r.courseModuleContentId,
	moduleId: (r) => r.courseModuleId,
	storedName: (r) => (r.storedName || "").toLowerCase(),
	packageType: (r) => (r.packageType || "").toLowerCase(),
	courses: (r) => formatAssociations(r.associations).toLowerCase(),
	phase: (r) => r.phase || "",
	existingCheckMs: (r) => r.timings?.existingCheckMs,
	downloadMs: (r) => r.timings?.downloadMs,
	audioExtractMs: (r) => r.timings?.audioExtractMs,
	uploadWavMs: (r) => r.timings?.uploadWavMs,
	submitMs: (r) => r.timings?.submitMs,
	pollWaitMs: (r) => r.timings?.pollWaitMs,
	harvestMs: (r) => r.timings?.harvestMs,
	uploadResultMs: (r) => r.timings?.uploadResultMs,
	segments: (r) => r.segmentCount,
	error: (r) => (r.error || "").toLowerCase()
});

const TranscriptBackfill = () => {
	const { isLoading: isCheckingAccess, isDeveloper: allowed } = useIsDeveloper();

	const [candidates, setCandidates] = useState([]);
	const [loadError, setLoadError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isRunning, setIsRunning] = useState(false);
	const [forceRerun, setForceRerun] = useState(false);
	const [logLines, setLogLines] = useState([]);
	const [progress, setProgress] = useState({ processed: 0, total: 0 });
	const [activeRunId, setActiveRunId] = useState(null);
	const [sortConfig, setSortConfig] = useState({ column: null, direction: "asc" });
	const [phaseFilter, setPhaseFilter] = useState("All");
	const [textFilter, setTextFilter] = useState("");
	const consoleRef = useRef(null);

	const { connection, registerHandler, onConnected } = useSignalR();

	const appendLog = useCallback((text) => {
		setLogLines((prev) => [...prev, `[${new Date().toLocaleTimeString()}] ${text}`]);
	}, []);

	const upsertCandidate = useCallback((contentId, patch) => {
		setCandidates((prev) => {
			const found = prev.some((row) => row.courseModuleContentId === contentId);
			if (found) {
				return prev.map((row) =>
					row.courseModuleContentId === contentId ? { ...row, ...patch } : row);
			}
			return prev;
		});
	}, []);

	const loadCandidates = useCallback(async () => {
		if (!allowed) {
			return;
		}
		setIsLoading(true);
		setLoadError("");
		try {
			const res = await fetch("/api/v3/TranscriptBackfill/Candidates", getFetchOptions());
			if (!res.ok) {
				setLoadError(`Failed to load candidates: ${res.status} ${res.statusText}`);
				return;
			}
			const data = await res.json();
			setCandidates(data.map((row) => ({
				...row,
				phase: row.hasExistingSuccessfulTranscript
					? CANDIDATE_PHASE.Harvested
					: row.hasExistingTranscript
						? CANDIDATE_PHASE.Failed
						: CANDIDATE_PHASE.Pending,
				timings: null,
				error: null
			})));
		}
		catch (err) {
			setLoadError(`Failed to load candidates: ${err.message}`);
		}
		finally {
			setIsLoading(false);
		}
	}, [allowed]);

	const handleSort = useCallback((column) => {
		setSortConfig((prev) => {
			if (prev.column !== column) {
				return { column, direction: "asc" };
			}
			if (prev.direction === "asc") {
				return { column, direction: "desc" };
			}
			// Third click clears the sort.
			return { column: null, direction: "asc" };
		});
	}, []);

	const clearFilters = useCallback(() => {
		setPhaseFilter("All");
		setTextFilter("");
		setSortConfig({ column: null, direction: "asc" });
	}, []);

	// Apply phase and text filters, then sort. Memoised so SignalR updates that
	// don't change candidates/filters/sort don't re-cost the work.
	const visibleCandidates = useMemo(() => {
		let rows = candidates;

		if (phaseFilter !== "All") {
			rows = rows.filter((r) => r.phase === phaseFilter);
		}

		const needle = textFilter.trim().toLowerCase();
		if (needle) {
			rows = rows.filter((r) =>
				(r.storedName || "").toLowerCase().includes(needle) ||
				(r.originalName || "").toLowerCase().includes(needle) ||
				(r.packageType || "").toLowerCase().includes(needle) ||
				(r.error || "").toLowerCase().includes(needle) ||
				formatAssociations(r.associations).toLowerCase().includes(needle));
		}

		if (sortConfig.column) {
			const accessor = SORT_ACCESSORS[sortConfig.column];
			if (accessor) {
				const dir = sortConfig.direction === "asc" ? 1 : -1;
				rows = [...rows].sort((a, b) => {
					const av = accessor(a);
					const bv = accessor(b);
					if (av == null && bv == null) return 0;
					if (av == null) return 1;     // nulls always last regardless of direction
					if (bv == null) return -1;
					if (av < bv) return -1 * dir;
					if (av > bv) return 1 * dir;
					return 0;
				});
			}
		}

		return rows;
	}, [candidates, phaseFilter, textFilter, sortConfig]);

	const downloadCsv = useCallback(() => {
		if (visibleCandidates.length === 0) {
			return;
		}
		const escape = (value) => {
			if (value == null) {
				return "";
			}
			const str = String(value);
			if (/[",\r\n]/.test(str)) {
				return `"${str.replace(/"/g, "\"\"")}"`;
			}
			return str;
		};

		const headers = [
			"ContentId", "ModuleId", "StoredName", "OriginalName", "PackageType",
			"Courses", "Phase",
			"ExistingCheckMs", "DownloadMs", "AudioExtractMs", "UploadWavMs",
			"SubmitMs", "PollWaitMs", "HarvestMs", "UploadResultMs",
			"SegmentCount", "Error"
		];

		const rows = visibleCandidates.map((row) => {
			const t = row.timings || {};
			return [
				row.courseModuleContentId,
				row.courseModuleId,
				row.storedName,
				row.originalName,
				row.packageType,
				formatAssociations(row.associations),
				row.phase,
				t.existingCheckMs,
				t.downloadMs,
				t.audioExtractMs,
				t.uploadWavMs,
				t.submitMs,
				t.pollWaitMs,
				t.harvestMs,
				t.uploadResultMs,
				row.segmentCount,
				row.error
			].map(escape).join(",");
		});

		// BOM so Excel correctly detects UTF-8.
		const body = "﻿" + [headers.join(","), ...rows].join("\r\n");
		const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, "").replace("T", "-");
		const link = document.createElement("a");
		link.href = url;
		link.download = `transcript-backfill-${timestamp}.csv`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}, [visibleCandidates]);

	useEffect(() => {
		loadCandidates();
	}, [loadCandidates]);

	useEffect(() => {
		if (consoleRef.current) {
			consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
		}
	}, [logLines]);

	// SignalR wiring. Per the comment in useUploadInProgress.js, this needs to live in
	// the same effect that owns the connection — splitting hook calls breaks the
	// handler-registry ref linkage.
	useEffect(() => {
		if (!connection || !allowed) {
			return undefined;
		}

		onConnected(async (conn) => {
			console.debug("TranscriptBackfill SignalR connected — joining group");
			await conn.invoke("JoinGroupAsync", SIGNALR_GROUP);
		});

		connection.onreconnected(async () => {
			console.debug("TranscriptBackfill SignalR reconnected — rejoining group");
			await connection.invoke("JoinGroupAsync", SIGNALR_GROUP);
			const runId = sessionStorage.getItem(RUN_ID_STORAGE_KEY);
			if (runId) {
				await refreshFromManifestSnapshot(runId);
			}
		});

		const unregisterStarted = registerHandler("TranscriptBackfillRunStarted", SIGNALR_GROUP, (message) => {
			const payload = message.payload || {};
			setActiveRunId(payload.runId);
			setIsRunning(true);
			setProgress({ processed: 0, total: payload.totalCandidates ?? 0 });
			appendLog(`Run ${payload.runId} started — ${payload.totalCandidates ?? 0} candidate(s).`);
			setCandidates((prev) => prev.map((row) => ({
				...row,
				phase: row.hasExistingSuccessfulTranscript && !forceRerun ? CANDIDATE_PHASE.Harvested : CANDIDATE_PHASE.Pending,
				timings: null,
				error: null
			})));
		});

		const unregisterCandidate = registerHandler("TranscriptBackfillCandidateChanged", SIGNALR_GROUP, (message) => {
			const payload = message.payload || {};
			upsertCandidate(payload.courseModuleContentId, {
				phase: payload.phase,
				timings: payload.timings,
				error: payload.error,
				segmentCount: payload.segmentCount ?? null
			});
			if (TERMINAL_PHASES.has(payload.phase)) {
				setProgress((prev) => ({
					processed: (payload.processed ?? prev.processed),
					total: payload.total ?? prev.total
				}));
			}
			const stamp = TERMINAL_PHASES.has(payload.phase) ? `${payload.processed ?? "?"}/${payload.total ?? "?"}` : "";
			appendLog(`${payload.storedName} → ${payload.phase}${stamp ? ` (${stamp})` : ""}${payload.error ? `: ${payload.error}` : ""}`);
		});

		const unregisterCompleted = registerHandler("TranscriptBackfillRunCompleted", SIGNALR_GROUP, (message) => {
			const payload = message.payload || {};
			setIsRunning(false);
			appendLog(
				`Run ${payload.runId} complete: ${payload.succeeded ?? 0} succeeded, ` +
				`${payload.failed ?? 0} failed, ${payload.skipped ?? 0} skipped, ` +
				`${payload.durationMs ?? 0}ms.`);
			sessionStorage.removeItem(RUN_ID_STORAGE_KEY);
		});

		return () => {
			unregisterStarted();
			unregisterCandidate();
			unregisterCompleted();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connection, allowed, onConnected, registerHandler]);

	const refreshFromManifestSnapshot = useCallback(async (runId) => {
		try {
			const res = await fetch(`/api/v3/TranscriptBackfill/Runs/${runId}`, getFetchOptions());
			if (!res.ok) {
				if (res.status === 404) {
					sessionStorage.removeItem(RUN_ID_STORAGE_KEY);
				}
				return;
			}
			const snapshot = await res.json();
			setActiveRunId(snapshot.runId);
			setProgress({
				processed: (snapshot.candidates || []).filter((c) => TERMINAL_PHASES.has(c.phase)).length,
				total: snapshot.totalCandidates ?? 0
			});
			setIsRunning(snapshot.phase === "preparing" || snapshot.phase === "submitting" || snapshot.phase === "polling" || snapshot.phase === "harvesting");
			setCandidates((prev) => prev.map((row) => {
				const fromServer = (snapshot.candidates || []).find((c) => c.courseModuleContentId === row.courseModuleContentId);
				if (!fromServer) {
					return row;
				}
				return {
					...row,
					phase: fromServer.phase,
					timings: fromServer.timings,
					error: fromServer.error,
					segmentCount: fromServer.segmentCount ?? null
				};
			}));
		}
		catch (err) {
			console.warn("Failed to refresh manifest snapshot:", err);
		}
	}, []);

	// On mount, if a runId is in sessionStorage, hydrate from the manifest snapshot so
	// the page reflects work that happened while we were disconnected.
	useEffect(() => {
		if (!allowed) {
			return;
		}
		const runId = sessionStorage.getItem(RUN_ID_STORAGE_KEY);
		if (runId) {
			setActiveRunId(runId);
			refreshFromManifestSnapshot(runId);
		}
	}, [allowed, refreshFromManifestSnapshot]);

	const startBackfill = useCallback(async (subsetIds = []) => {
		if (isRunning) {
			return;
		}
		setIsRunning(true);
		setLogLines([]);
		try {
			const options = getFetchOptions({
				method: "POST",
				body: { forceRerun, courseModuleContentIds: subsetIds }
			});
			const response = await fetch("/api/v3/TranscriptBackfill/Runs", options);
			if (!response.ok) {
				appendLog(`Run failed to start: ${response.status} ${response.statusText}`);
				setIsRunning(false);
				return;
			}
			const payload = await response.json();
			if (!payload.runId) {
				appendLog("Run failed to start: server did not return a runId.");
				setIsRunning(false);
				return;
			}
			sessionStorage.setItem(RUN_ID_STORAGE_KEY, payload.runId);
			setActiveRunId(payload.runId);
			appendLog(`Run ${payload.runId} queued. Awaiting SignalR updates...`);
		}
		catch (err) {
			appendLog(`Run errored: ${err.message}`);
			setIsRunning(false);
		}
	}, [appendLog, forceRerun, isRunning]);

	const abortBackfill = useCallback(async () => {
		const runId = activeRunId || sessionStorage.getItem(RUN_ID_STORAGE_KEY);
		if (!runId) {
			return;
		}
		try {
			await fetch(`/api/v3/TranscriptBackfill/Runs/${runId}`, getFetchOptions({ method: "DELETE" }));
			appendLog("Cancel requested. Handlers will stop after their current step.");
		}
		catch (err) {
			appendLog(`Cancel request failed: ${err.message}`);
		}
	}, [activeRunId, appendLog]);

	if (isCheckingAccess) {
		return null;
	}
	if (!allowed) {
		return <Navigate to="/dashboard" replace />;
	}

	const SortableHeader = ({ column, label }) => {
		const active = sortConfig.column === column;
		const indicator = active ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : "";
		return (
			<th onClick={() => handleSort(column)} style={{ cursor: "pointer", userSelect: "none" }}>
				{label}{indicator}
			</th>
		);
	};

	return (
		<div className="transcript-backfill">
			<header className="transcript-backfill__header">
				<h1>Transcript Backfill</h1>
				<p>
					Re-runs the transcription pipeline against every active CourseModuleContent in blob storage.
					Each row writes a <code>transcriptionResult.json</code> to <code>course-transcripts/&lt;StoredName&gt;/</code>.
				</p>
			</header>

			<section className="transcript-backfill__controls">
				<label>
					<input
						type="checkbox"
						checked={forceRerun}
						onChange={(e) => setForceRerun(e.target.checked)}
						disabled={isRunning}
					/>
					Re-run even when an existing transcript reports Success=true
				</label>
				<button type="button" onClick={() => startBackfill()} disabled={isRunning || isLoading || candidates.length === 0}>
					{isRunning ? "Running..." : "Start backfill"}
				</button>
				<button type="button" onClick={abortBackfill} disabled={!isRunning}>
					Abort
				</button>
				<button type="button" onClick={loadCandidates} disabled={isRunning || isLoading}>
					Reload list
				</button>
				<button type="button" onClick={downloadCsv} disabled={candidates.length === 0 || isLoading}>
					Download CSV
				</button>
				<span className="transcript-backfill__progress">
					{progress.total > 0 ? `${progress.processed} / ${progress.total}` : `${candidates.length} candidate(s)`}
				</span>
				{activeRunId && (
					<span className="transcript-backfill__run-id" title={activeRunId}>
						Run: {activeRunId.slice(0, 8)}
					</span>
				)}
			</section>

			{loadError && <div className="transcript-backfill__error">{loadError}</div>}

			<section className="transcript-backfill__filters" style={{ display: "flex", gap: "1em", alignItems: "center", flexWrap: "wrap", marginBottom: "0.5em" }}>
				<label>
					Phase:{" "}
					<select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}>
						<option value="All">All</option>
						{Object.values(CANDIDATE_PHASE).map((p) => (
							<option key={p} value={p}>{p}</option>
						))}
					</select>
				</label>
				<label>
					Search:{" "}
					<input
						type="text"
						value={textFilter}
						onChange={(e) => setTextFilter(e.target.value)}
						placeholder="Stored name, package, error..."
						style={{ minWidth: "20em" }}
					/>
				</label>
				<button type="button" onClick={clearFilters}>Clear filters</button>
				<span>Showing {visibleCandidates.length} of {candidates.length}</span>
			</section>

			<section className="transcript-backfill__table-wrapper">
				<table className="transcript-backfill__table">
					<thead>
						<tr>
							<th>Run</th>
							<SortableHeader column="contentId" label="Content ID" />
							<SortableHeader column="moduleId" label="Module ID" />
							<SortableHeader column="storedName" label="Stored Name" />
							<SortableHeader column="packageType" label="Package" />
							<SortableHeader column="courses" label="Courses" />
							<SortableHeader column="phase" label="Phase" />
							<SortableHeader column="existingCheckMs" label="Existing Check" />
							<SortableHeader column="downloadMs" label="Download" />
							<SortableHeader column="audioExtractMs" label="Audio Extract" />
							<SortableHeader column="uploadWavMs" label="Upload WAV" />
							<SortableHeader column="submitMs" label="Submit" />
							<SortableHeader column="pollWaitMs" label="Poll Wait" />
							<SortableHeader column="harvestMs" label="Harvest" />
							<SortableHeader column="uploadResultMs" label="Upload Result" />
							<SortableHeader column="segments" label="Segments" />
							<SortableHeader column="error" label="Error" />
						</tr>
					</thead>
					<tbody>
						{isLoading && (
							<tr><td colSpan={17}>Loading...</td></tr>
						)}
						{!isLoading && candidates.length === 0 && (
							<tr><td colSpan={17}>No candidates found.</td></tr>
						)}
						{!isLoading && candidates.length > 0 && visibleCandidates.length === 0 && (
							<tr><td colSpan={17}>No rows match the current filters.</td></tr>
						)}
						{visibleCandidates.map((row) => {
							const phase = row.phase || CANDIDATE_PHASE.Pending;
							const timings = row.timings || {};
							return (
								<tr key={row.courseModuleContentId} className={`transcript-backfill__row transcript-backfill__row--${phase.toLowerCase()}`}>
									<td>
										<button
											type="button"
											onClick={() => startBackfill([row.courseModuleContentId])}
											disabled={isRunning || isLoading}
											title="Run backfill against just this course"
										>
											Run
										</button>
									</td>
									<td>{row.courseModuleContentId}</td>
									<td>{row.courseModuleId}</td>
									<td title={row.originalName}>{row.storedName}</td>
									<td>{row.packageType}</td>
									<td>{formatAssociations(row.associations)}</td>
									<td>{phase}</td>
									<td>{formatMs(timings.existingCheckMs)}</td>
									<td>{formatMs(timings.downloadMs)}</td>
									<td>{formatMs(timings.audioExtractMs)}</td>
									<td>{formatMs(timings.uploadWavMs)}</td>
									<td>{formatMs(timings.submitMs)}</td>
									<td>{formatMs(timings.pollWaitMs)}</td>
									<td>{formatMs(timings.harvestMs)}</td>
									<td>{formatMs(timings.uploadResultMs)}</td>
									<td>{row.segmentCount != null ? row.segmentCount : "—"}</td>
									<td className="transcript-backfill__error-cell">{row.error || ""}</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</section>

			<section className="transcript-backfill__console-wrapper">
				<h2>Console</h2>
				<pre ref={consoleRef} className="transcript-backfill__console">
					{logLines.join("\n")}
				</pre>
			</section>
		</div>
	);
};

export default TranscriptBackfill;
